import React, { useState, useMemo, useRef } from 'react';
import { Pressable, Text, View, useWindowDimensions } from 'react-native';
import { useColorScheme } from 'nativewind';
import RenderHtml from 'react-native-render-html';
import svgIcons from '../../assets';
import { Icon, ModalInfo, Tooltip } from '../index';
import { HelpdeskIssueSearchResult } from '../../client/interfaces';
import { handleLinkPress } from '../../utils';
import { isMobile } from '../../utils/responsive';
import { highlightHtml } from '../../utils/highlightHtml';

const MemoizedRenderHtml = React.memo(RenderHtml);

const renderersProps = {
  a: { onPress: (_event: unknown, href: string) => handleLinkPress(href) },
};

const HelpdeskSectionHeader: React.FC<{
  item: HelpdeskIssueSearchResult;
  searchText?: string;
}> = ({ item, searchText }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [modalInfoVisible, setModalInfoVisible] = useState(false);
  const tooltipHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTooltip = () => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    setTooltipVisible(true);
  };
  const hideTooltip = () => {
    tooltipHideTimer.current = setTimeout(() => setTooltipVisible(false), 100);
  };
  const { colorScheme } = useColorScheme();
  const { width } = useWindowDimensions();
  const tooltipBaseStyle = useMemo(
    () => ({
      color: colorScheme === 'dark' ? '#101010' : '#f6f6f6',
      fontSize: 12,
      fontFamily: 'sans-400',
      lineHeight: 18,
    }),
    [colorScheme],
  );
  const tooltipTagsStyles = useMemo(
    () => ({
      a: {
        color: colorScheme === 'dark' ? '#623BFF' : '#EDF115',
        textDecorationLine: 'none' as const,
      },
      mark: {
        backgroundColor: colorScheme === 'dark' ? '#EDF115' : '#8509ff',
        color: colorScheme === 'dark' ? '#101010' : '#FAFAFA',
      },
    }),
    [colorScheme],
  );

  return (
    <View className="flex-row items-center bg-neutral-50 dark:bg-neutral-950 pb-3 pt-3 pl-[30px] pr-[30px]">
      <Text className="text-body sm:text-h3 text-neutral-950 dark:text-neutral-50 font-[sans-700] mr-3">
        {item.topicName}
      </Text>
      {item.topicDescription && item.helpdeskIssues.length > 0 ? (
        <Pressable
          testID="helpdesk-topic-info-native"
          style={{ position: 'relative' }}
          onHoverIn={() => !isMobile && showTooltip()}
          onHoverOut={() => !isMobile && hideTooltip()}
          onPress={() => isMobile && setModalInfoVisible(true)}
        >
          <Icon
            icon={
              searchText && item.topicDescription.toLowerCase().includes(searchText.toLowerCase())
                ? svgIcons.infoIconPrimary
                : svgIcons.infoIcon
            }
            mobileSize={{ width: 24, height: 24 }}
            webSize={{ width: 32, height: 32 }}
          />
          {tooltipVisible && (
            <Tooltip>
              <MemoizedRenderHtml
                contentWidth={width}
                source={{ html: highlightHtml(item.topicDescription, searchText) }}
                baseStyle={tooltipBaseStyle}
                renderersProps={renderersProps}
                tagsStyles={tooltipTagsStyles}
              />
            </Tooltip>
          )}
          <ModalInfo
            testID="helpdesk-issue-section-modal-native"
            showModal={modalInfoVisible}
            closeModal={() => setModalInfoVisible(false)}
            text={item.topicDescription}
            search={searchText}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

export default HelpdeskSectionHeader;
